/**
 * SeasonReplay — watch one simulated season unfold week by week.
 *
 * Teams slide up and down as standings change each week.
 * The playoff cutline stays fixed — teams race across it.
 *
 * This shows ONE simulated season. 10,000 are run to compute playoff odds.
 * That framing is made explicit in the UI.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";

const TEAMS = [
  { id: 1,  name: 'LovingChicken',   strength: 0.90 },
  { id: 2,  name: 'jonp24',          strength: 0.90 },
  { id: 3,  name: 'preeceman',       strength: 0.81 },
  { id: 4,  name: 'ROMANeseCalcio',  strength: 0.80 },
  { id: 5,  name: 'Bisto',           strength: 0.76 },
  { id: 6,  name: 'Walshys94',       strength: 0.44, isMe: true },
  { id: 7,  name: 'middlein',        strength: 0.30 },
  { id: 8,  name: 'BewareTheHare',   strength: 0.27 },
  { id: 9,  name: 'gmesa25',         strength: 0.27 },
  { id: 10, name: 'The Biscuits',    strength: 0.22 },
  { id: 11, name: 'Coulda been',     strength: 0.21 },
  { id: 12, name: 'Wangs n Thangs',  strength: 0.12 },
];
const MY_ID = 6;
const TOTAL_WEEKS = 14;
const PLAYOFF_SLOTS = 6;
const ROW_H = 46; // px per team row in the race

// ── Schedule ──────────────────────────────────────────────────────────────────
function buildSchedule() {
  const schedule = [];
  for (let w = 0; w < TOTAL_WEEKS; w++) {
    const used = new Set();
    const matchups = [];
    const shuffled = [...TEAMS].sort((a, b) => {
      // deterministic but varied shuffle per week
      const ha = (a.id * 31 + w * 17 + 7) % TEAMS.length;
      const hb = (b.id * 31 + w * 17 + 7) % TEAMS.length;
      return ha - hb;
    });
    for (const t of shuffled) {
      if (used.has(t.id)) continue;
      const opp = shuffled.find(o => !used.has(o.id) && o.id !== t.id);
      if (!opp) continue;
      used.add(t.id); used.add(opp.id);
      matchups.push([t.id, opp.id]);
    }
    schedule.push(matchups);
  }
  return schedule;
}

const BASE_SCHEDULE = buildSchedule();

// ── Simulation ────────────────────────────────────────────────────────────────
function gaussian() {
  const u = Math.random(), v = Math.random();
  return Math.sqrt(-2 * Math.log(Math.max(u, 1e-10))) * Math.cos(2 * Math.PI * v);
}

function sampleScore(strength) {
  const base = 82 + strength * 58;
  return Math.max(40, base + gaussian() * 24);
}

function simulateSeason() {
  const wins = {}, losses = {}, pf = {};
  TEAMS.forEach(t => { wins[t.id] = 0; losses[t.id] = 0; pf[t.id] = 0; });

  const weekSnapshots = [];

  // Week 0 — pre-season standings (everyone 0-0, sorted by projected strength)
  const preSeasonStandings = TEAMS.map(t => ({ id: t.id, wins: 0, losses: 0, pf: 0 }));
  weekSnapshots.push({ week: 0, results: [], standings: preSeasonStandings });

  for (let w = 0; w < TOTAL_WEEKS; w++) {
    const results = BASE_SCHEDULE[w].map(([aId, bId]) => {
      const a = TEAMS.find(t => t.id === aId);
      const b = TEAMS.find(t => t.id === bId);
      const sA = sampleScore(a.strength);
      const sB = sampleScore(b.strength);
      const winner = sA >= sB ? aId : bId;
      wins[winner]++;
      losses[winner === aId ? bId : aId]++;
      pf[aId] += sA;
      pf[bId] += sB;
      return { aId, bId, scoreA: sA, scoreB: sB, winnerId: winner };
    });

    const standings = TEAMS
      .map(t => ({ id: t.id, wins: wins[t.id], losses: losses[t.id], pf: pf[t.id] }))
      .sort((a, b) => b.wins - a.wins || b.pf - a.pf);

    weekSnapshots.push({ week: w + 1, results, standings });
  }

  const finalStandings = weekSnapshots[TOTAL_WEEKS].standings;
  const playoffTeams = new Set(finalStandings.slice(0, PLAYOFF_SLOTS).map(t => t.id));
  return { weeks: weekSnapshots, playoffTeams, finalStandings };
}

// ── Sliding standings race ─────────────────────────────────────────────────────
function StandingsRace({ standings, isFinished, playoffTeams, week }) {
  // standings is already sorted. Build a rank map for absolute positioning.
  const rankOf = {};
  standings.forEach((s, i) => { rankOf[s.id] = i; });

  // Max wins for bar scaling
  const maxWins = Math.max(...standings.map(s => s.wins), 1);

  const containerH = TEAMS.length * ROW_H + 24; // +24 for the cutline label

  return (
    <div style={{ position: 'relative', height: containerH }}>

      {/* Fixed playoff cutline — always between slot 6 and 7 */}
      <div style={{
        position: 'absolute',
        top: PLAYOFF_SLOTS * ROW_H + 2,
        left: 0, right: 0,
        display: 'flex', alignItems: 'center', gap: '8px',
        zIndex: 5, pointerEvents: 'none',
      }}>
        <div style={{ flex: 1, borderTop: '1px dashed rgba(255,255,255,0.15)' }} />
        <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Playoff line · Top {PLAYOFF_SLOTS}
        </span>
        <div style={{ flex: 1, borderTop: '1px dashed rgba(255,255,255,0.15)' }} />
      </div>

      {/* Team rows — absolutely positioned, slide via top transition */}
      {TEAMS.map(team => {
        const rank = rankOf[team.id];
        const entry = standings.find(s => s.id === team.id) || { wins: 0, losses: 0, pf: 0 };
        const inPlayoffs = rank < PLAYOFF_SLOTS;
        const eliminated = isFinished && !inPlayoffs;
        const clinched = isFinished && inPlayoffs;
        const barWidth = week === 0 ? 0 : (entry.wins / maxWins) * 100;

        return (
          <div
            key={team.id}
            style={{
              position: 'absolute',
              top: rank * ROW_H + (rank >= PLAYOFF_SLOTS ? 20 : 0), // offset below cutline label
              left: 0, right: 0,
              height: ROW_H - 4,
              transition: 'top 0.5s cubic-bezier(0.16,1,0.3,1)',
              display: 'grid',
              gridTemplateColumns: '22px 1fr 2.5fr 52px',
              alignItems: 'center',
              gap: '10px',
              opacity: eliminated ? 0.35 : 1,
              padding: '0 4px',
            }}
          >
            {/* Rank number */}
            <div style={{
              fontSize: '12px',
              fontWeight: team.isMe ? 700 : 400,
              color: team.isMe ? '#FF9500' : 'rgba(255,255,255,0.2)',
              textAlign: 'right',
              transition: 'color 0.3s',
            }}>
              {rank + 1}
            </div>

            {/* Team name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
              <span style={{
                fontSize: team.isMe ? '13px' : '12px',
                fontWeight: team.isMe ? 700 : 400,
                color: team.isMe ? '#FFFFFF' : inPlayoffs ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.25)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                transition: 'color 0.3s',
              }}>
                {team.name}
              </span>
              {team.isMe && (
                <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', background: '#FF9500', color: '#000', borderRadius: '3px', padding: '1px 4px', flexShrink: 0 }}>
                  YOU
                </span>
              )}
              {clinched && <span style={{ fontSize: '11px', flexShrink: 0 }}>✓</span>}
            </div>

            {/* Win bar */}
            <div style={{ position: 'relative', height: '5px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${barWidth}%`,
                background: team.isMe
                  ? 'linear-gradient(90deg, #FF9500, #FFB800)'
                  : inPlayoffs
                    ? `rgba(255,255,255,${0.28 + (PLAYOFF_SLOTS - rank) * 0.06})`
                    : 'rgba(255,255,255,0.1)',
                borderRadius: '3px',
                transition: 'width 0.5s cubic-bezier(0.16,1,0.3,1)',
                boxShadow: team.isMe ? '0 0 6px rgba(255,149,0,0.4)' : 'none',
              }} />
            </div>

            {/* Record */}
            <div style={{
              fontSize: '12px',
              fontWeight: team.isMe ? 700 : 400,
              color: team.isMe ? '#FF9500' : inPlayoffs ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.18)',
              textAlign: 'right',
              whiteSpace: 'nowrap',
              transition: 'color 0.3s',
            }}>
              {entry.wins}–{entry.losses}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Your matchup card ─────────────────────────────────────────────────────────
function MatchupCard({ result }) {
  if (!result) return null;
  const { aId, bId, scoreA, scoreB, winnerId } = result;
  const myId = aId === MY_ID ? 'a' : 'b';
  const myScore = myId === 'a' ? scoreA : scoreB;
  const oppScore = myId === 'a' ? scoreB : scoreA;
  const oppId = myId === 'a' ? bId : aId;
  const oppTeam = TEAMS.find(t => t.id === oppId);
  const won = winnerId === MY_ID;

  return (
    <div style={{
      background: won ? 'rgba(52,199,89,0.07)' : 'rgba(255,59,48,0.07)',
      border: `1px solid ${won ? 'rgba(52,199,89,0.18)' : 'rgba(255,59,48,0.14)'}`,
      borderRadius: '12px', padding: '12px 18px',
      display: 'flex', alignItems: 'center', gap: '12px',
      fontFamily: ff, transition: 'all 0.3s',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1px', color: won ? '#34C759' : '#FF3B30', textTransform: 'uppercase', minWidth: '24px' }}>
        {won ? 'W' : 'L'}
      </div>
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: '#FFF' }}>Walshys94</span>
        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}> vs {oppTeam?.name}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '15px', fontWeight: 700, color: won ? '#34C759' : 'rgba(255,255,255,0.45)', letterSpacing: '-0.01em' }}>
          {myScore.toFixed(1)}
        </span>
        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.2)' }}>–</span>
        <span style={{ fontSize: '15px', fontWeight: won ? 400 : 700, color: won ? 'rgba(255,255,255,0.3)' : '#FF3B30', letterSpacing: '-0.01em' }}>
          {oppScore.toFixed(1)}
        </span>
      </div>
    </div>
  );
}

// ── All matchups mini-grid ────────────────────────────────────────────────────
function AllMatchups({ results }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', fontFamily: ff }}>
      {results.map(({ aId, bId, scoreA, scoreB, winnerId }) => {
        const a = TEAMS.find(t => t.id === aId);
        const b = TEAMS.find(t => t.id === bId);
        const involved = aId === MY_ID || bId === MY_ID;
        return (
          <div key={`${aId}-${bId}`} style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '4px 7px', borderRadius: '5px',
            background: involved ? 'rgba(255,149,0,0.05)' : 'rgba(255,255,255,0.02)',
            border: involved ? '1px solid rgba(255,149,0,0.1)' : '1px solid transparent',
            fontSize: '11px',
          }}>
            <span style={{ color: winnerId === aId ? '#FFF' : 'rgba(255,255,255,0.25)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: winnerId === aId ? 500 : 400 }}>
              {a?.name}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '10px', flexShrink: 0 }}>
              {scoreA.toFixed(0)}–{scoreB.toFixed(0)}
            </span>
            <span style={{ color: winnerId === bId ? '#FFF' : 'rgba(255,255,255,0.25)', flex: 1, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: winnerId === bId ? 500 : 400 }}>
              {b?.name}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
// ── Trajectory chart — spaghetti plot of all past seasons ────────────────────
function TrajectoryChart({ pastSeasons, currentTrajectory, week }) {
  const W = 580, H = 200;
  const PAD = { l: 28, r: 12, t: 12, b: 24 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;

  // Map week → x, wins → y
  const x = (w) => PAD.l + (w / TOTAL_WEEKS) * chartW;
  const y = (wins) => PAD.t + chartH - (wins / TOTAL_WEEKS) * chartH;

  // Playoff threshold line ~7 wins (playoff_slots/teams * total_weeks)
  const playoffY = y(Math.round(PLAYOFF_SLOTS / TEAMS.length * TOTAL_WEEKS));

  // Build SVG path from a trajectory (array of win totals per week, week 0..14)
  function makePath(traj) {
    return traj.map((wins, w) => `${w === 0 ? 'M' : 'L'} ${x(w).toFixed(1)},${y(wins).toFixed(1)}`).join(' ');
  }

  const madeCount = pastSeasons.filter(s => s.madePlayoffs).length;
  const total = pastSeasons.length;

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
        <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase' }}>
          Season trajectories — each line = one simulated season
        </div>
        {total > 0 && (
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontFamily: ff }}>
            <span style={{ color: '#FF9500', fontWeight: 700 }}>{madeCount}</span>
            <span style={{ color: 'rgba(255,255,255,0.2)' }}> / {total} made playoffs</span>
            {total >= 3 && (
              <span style={{ color: '#FF9500', fontWeight: 700, marginLeft: '6px' }}>
                ({Math.round(madeCount / total * 100)}%)
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ background: '#0D0D0F', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
          {/* Grid lines */}
          {[0, 2, 4, 6, 8, 10, 12, 14].map(w => (
            <line key={w} x1={x(w)} y1={PAD.t} x2={x(w)} y2={PAD.t + chartH}
              stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
          ))}

          {/* Playoff threshold line */}
          <line x1={PAD.l} y1={playoffY} x2={W - PAD.r} y2={playoffY}
            stroke="rgba(52,199,89,0.3)" strokeWidth="1" strokeDasharray="4,4" />
          <text x={W - PAD.r - 2} y={playoffY - 4} fill="rgba(52,199,89,0.5)"
            fontSize="9" textAnchor="end" fontFamily={ff}>
            playoff line
          </text>

          {/* Past season paths */}
          {pastSeasons.map((s, i) => (
            <path key={i} d={makePath(s.trajectory)}
              fill="none"
              stroke={s.madePlayoffs ? 'rgba(255,149,0,0.18)' : 'rgba(255,59,48,0.12)'}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {/* Current season — animated up to current week */}
          {currentTrajectory.length > 0 && (
            <>
              <path d={makePath(currentTrajectory.slice(0, week + 1))}
                fill="none"
                stroke="rgba(255,255,255,0.7)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Moving dot */}
              <circle
                cx={x(Math.min(week, currentTrajectory.length - 1))}
                cy={y(currentTrajectory[Math.min(week, currentTrajectory.length - 1)] || 0)}
                r="4" fill="#FF9500"
              />
            </>
          )}

          {/* Y axis labels */}
          {[0, 4, 7, 10, 14].map(wins => (
            <text key={wins} x={PAD.l - 4} y={y(wins) + 4}
              fill="rgba(255,255,255,0.18)" fontSize="9" textAnchor="end" fontFamily={ff}>
              {wins}W
            </text>
          ))}

          {/* X axis labels */}
          {[1, 4, 7, 10, 14].map(w => (
            <text key={w} x={x(w)} y={H - 6}
              fill="rgba(255,255,255,0.18)" fontSize="9" textAnchor="middle" fontFamily={ff}>
              {w}
            </text>
          ))}
        </svg>
      </div>

      {total === 0 && (
        <div style={{ textAlign: 'center', fontSize: '12px', color: 'rgba(255,255,255,0.2)', marginTop: '8px', fontFamily: ff }}>
          Hit Resimulate a few times — each season adds a line. Orange = made playoffs, red = missed.
        </div>
      )}
    </div>
  );
}

export default function SeasonReplay() {
  const [season, setSeason] = useState(null);
  const [week, setWeek] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(700);
  const [showAll, setShowAll] = useState(false);
  const [pastSeasons, setPastSeasons] = useState([]); // accumulates across resimulates
  const intervalRef = useRef(null);

  const toTrajectory = useCallback((sim) => ({
    trajectory: sim.weeks.map(snap => snap.standings.find(s => s.id === MY_ID)?.wins || 0),
    madePlayoffs: sim.playoffTeams.has(MY_ID),
  }), []);

  const newSeason = useCallback((keepPast = true) => {
    const sim = simulateSeason();
    if (keepPast) {
      setSeason(prev => {
        // Archive the outgoing season into the trajectory chart
        if (prev) {
          setPastSeasons(ps => [...ps.slice(-99), toTrajectory(prev)]);
        }
        return sim;
      });
    } else {
      setSeason(sim);
    }
    setWeek(0);
    setPlaying(false);
    setShowAll(false);
    clearInterval(intervalRef.current);
  }, [toTrajectory]);

  useEffect(() => {
    // Pre-populate trajectory chart with 100 simulations immediately
    // (mirrors what the real engine already computes across 10,000 runs)
    const preRun = [];
    for (let i = 0; i < 100; i++) {
      preRun.push(toTrajectory(simulateSeason()));
    }
    setPastSeasons(preRun);
    // Then start a fresh animated season
    const first = simulateSeason();
    setSeason(first);
    setWeek(0);
  }, []);

  // Auto-play after new season
  useEffect(() => {
    if (season && week === 0) {
      const t = setTimeout(() => setPlaying(true), 400);
      return () => clearTimeout(t);
    }
  }, [season]);

  // Advance week
  useEffect(() => {
    if (!playing || !season) return;
    intervalRef.current = setInterval(() => {
      setWeek(w => {
        if (w >= TOTAL_WEEKS) { setPlaying(false); clearInterval(intervalRef.current); return w; }
        return w + 1;
      });
    }, speed);
    return () => clearInterval(intervalRef.current);
  }, [playing, speed, season]);

  const snap = season?.weeks[week] || null;
  const standings = snap?.standings || TEAMS.map(t => ({ id: t.id, wins: 0, losses: 0, pf: 0 }));
  const myResult = snap?.results.find(r => r.aId === MY_ID || r.bId === MY_ID);
  const myEntry = standings.find(s => s.id === MY_ID);
  const myRank = standings.findIndex(s => s.id === MY_ID) + 1;
  const inPlayoffs = myRank <= PLAYOFF_SLOTS;
  const isFinished = week >= TOTAL_WEEKS;
  const madePlayoffs = isFinished && season?.playoffTeams.has(MY_ID);

  // Current season trajectory (wins per week for my team)
  const currentTrajectory = season
    ? season.weeks.map(snap => snap.standings.find(s => s.id === MY_ID)?.wins || 0)
    : [];

  return (
    <div style={{ minHeight: '100vh', background: '#080808', fontFamily: ff }}>
      <div style={{ maxWidth: '660px', margin: '0 auto', padding: '44px 24px 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '3px', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', marginBottom: '8px' }}>
            Season Replay · One possible outcome
          </div>
          <div style={{ fontSize: '36px', fontWeight: 700, color: '#FFF', letterSpacing: '-0.03em', lineHeight: 1.05, marginBottom: '8px' }}>
            Watch your season play out.
          </div>
          <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.55 }}>
            The chart shows 100 of the 10,000 seasons we simulated —{' '}
            <span style={{ color: '#FF9500' }}>orange lines made playoffs</span>, red lines missed.
            Below, one season animates in detail. Hit Resimulate to swap which one you're watching.
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {!isFinished ? (
            <button
              onClick={() => setPlaying(p => !p)}
              style={{
                background: playing ? 'rgba(255,255,255,0.07)' : '#FFF',
                color: playing ? '#FFF' : '#000',
                border: 'none', borderRadius: '10px', padding: '10px 18px',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: ff,
                transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {playing ? '⏸ Pause' : week === 0 ? '▶ Play' : '▶ Resume'}
            </button>
          ) : null}
          <button
            onClick={newSeason}
            style={{
              background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 18px',
              fontSize: '14px', fontWeight: 500, cursor: 'pointer', fontFamily: ff, transition: 'all 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
          >
            ↺ Resimulate
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>Speed</span>
            {[['Slow', 1100], ['Normal', 700], ['Fast', 300]].map(([label, ms]) => (
              <button key={label} onClick={() => setSpeed(ms)} style={{
                background: speed === ms ? 'rgba(255,255,255,0.1)' : 'none',
                color: speed === ms ? '#FFF' : 'rgba(255,255,255,0.3)',
                border: 'none', borderRadius: '6px', padding: '5px 9px',
                fontSize: '12px', fontWeight: speed === ms ? 600 : 400,
                cursor: 'pointer', fontFamily: ff,
              }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Week indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(255,255,255,0.55)', minWidth: '72px' }}>
            {week === 0 ? 'Pre-season' : isFinished ? 'Final' : `Week ${week}`}
          </div>
          <div style={{ flex: 1, height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: '2px',
              width: `${(week / TOTAL_WEEKS) * 100}%`,
              background: 'rgba(255,255,255,0.5)',
              transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)', minWidth: '44px', textAlign: 'right' }}>
            of {TOTAL_WEEKS}
          </div>
        </div>

        {/* Outcome banner (finished) */}
        {isFinished && (
          <div style={{
            background: madePlayoffs ? 'rgba(52,199,89,0.09)' : 'rgba(255,59,48,0.08)',
            border: `1px solid ${madePlayoffs ? 'rgba(52,199,89,0.22)' : 'rgba(255,59,48,0.18)'}`,
            borderRadius: '12px', padding: '14px 20px', marginBottom: '16px',
            display: 'flex', alignItems: 'center', gap: '14px',
          }}>
            <div style={{ fontSize: '26px' }}>{madePlayoffs ? '✅' : '❌'}</div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#FFF', marginBottom: '2px' }}>
                {madePlayoffs
                  ? `Walshys94 makes it — finished ${myRank}${['st','nd','rd'][myRank-1]||'th'} at ${myEntry?.wins}-${myEntry?.losses}`
                  : `Walshys94 misses — finished ${myRank}${['st','nd','rd'][myRank-1]||'th'} at ${myEntry?.wins}-${myEntry?.losses}`}
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
                {madePlayoffs
                  ? 'This outcome happens in about 44% of simulations'
                  : `Needed ${6 - (myEntry?.wins || 0) > 0 ? `~${Math.max(1, 6 - (myEntry?.wins || 0))} more win${Math.max(1,6-(myEntry?.wins||0))>1?'s':''}` : 'a better record'} · hit Resimulate`}
              </div>
            </div>
          </div>
        )}

        {/* In-play status */}
        {!isFinished && week > 0 && (
          <div style={{
            fontSize: '13px',
            color: inPlayoffs ? 'rgba(52,199,89,0.7)' : 'rgba(255,149,0,0.7)',
            marginBottom: '12px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span style={{ fontSize: '10px' }}>{inPlayoffs ? '●' : '○'}</span>
            {inPlayoffs
              ? `Walshys94 is currently in playoff position (${myRank}${['st','nd','rd'][myRank-1]||'th'})`
              : `Walshys94 is currently out — ${myRank - PLAYOFF_SLOTS} spot${myRank - PLAYOFF_SLOTS > 1 ? 's' : ''} behind`}
          </div>
        )}

        {/* Your matchup */}
        {myResult && week > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', marginBottom: '6px' }}>
              Your matchup · Week {week}
            </div>
            <MatchupCard result={myResult} />
          </div>
        )}

        {/* All matchups toggle */}
        {snap?.results?.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setShowAll(v => !v)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.28)', fontSize: '12px', cursor: 'pointer', fontFamily: ff, padding: '0 0 8px 0' }}
            >
              {showAll ? '▾' : '▸'} All matchups this week
            </button>
            {showAll && <AllMatchups results={snap.results} />}
          </div>
        )}

        {/* TRAJECTORY CHART — 100 seasons already drawn from the engine */}
        <TrajectoryChart
          pastSeasons={pastSeasons}
          currentTrajectory={currentTrajectory}
          week={week}
        />

        {/* THE RACE — sliding standings */}
        <div style={{
          background: '#0D0D0F',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.06)',
          padding: '18px 20px',
        }}>
          <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', marginBottom: '16px' }}>
            {isFinished ? 'Final standings' : week === 0 ? 'Starting order' : `Standings after week ${week}`}
          </div>
          <StandingsRace
            standings={standings}
            isFinished={isFinished}
            playoffTeams={season?.playoffTeams}
            week={week}
          />
        </div>

      </div>
    </div>
  );
}
